import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import MarkdownContent from "@/components/MarkdownContent";
import {
  TextB, TextItalic, TextHOne, TextHTwo, ListBullets, ListNumbers, LinkSimple, Quotes,
} from "@phosphor-icons/react";

// Markdown editor: formatting toolbar + textarea + live sanitized preview.
export default function MarkdownEditor({ value = "", onChange }) {
  const ref = useRef(null);

  const wrap = (before, after = "") => {
    const el = ref.current;
    if (!el) return;
    const s = el.selectionStart ?? value.length;
    const e = el.selectionEnd ?? value.length;
    const sel = value.slice(s, e);
    const next = value.slice(0, s) + before + sel + after + value.slice(e);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = s + before.length;
      el.selectionEnd = s + before.length + sel.length;
    });
  };

  const prefixLine = (prefix) => {
    const el = ref.current;
    if (!el) return;
    const s = el.selectionStart ?? value.length;
    const lineStart = value.lastIndexOf("\n", s - 1) + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = s + prefix.length;
    });
  };

  const TOOLS = [
    { id: "bold", Icon: TextB, title: "Bold", fn: () => wrap("**", "**") },
    { id: "italic", Icon: TextItalic, title: "Italic", fn: () => wrap("_", "_") },
    { id: "h1", Icon: TextHOne, title: "Heading 1", fn: () => prefixLine("# ") },
    { id: "h2", Icon: TextHTwo, title: "Heading 2", fn: () => prefixLine("## ") },
    { id: "ul", Icon: ListBullets, title: "Bulleted list", fn: () => prefixLine("- ") },
    { id: "ol", Icon: ListNumbers, title: "Numbered list", fn: () => prefixLine("1. ") },
    { id: "quote", Icon: Quotes, title: "Quote", fn: () => prefixLine("> ") },
    { id: "link", Icon: LinkSimple, title: "Link", fn: () => wrap("[", "](https://)") },
  ];

  return (
    <div data-testid="markdown-editor">
      <div className="flex flex-wrap items-center gap-1 border border-border rounded-t-sm bg-secondary/50 p-1.5">
        {TOOLS.map(({ id, Icon, title, fn }) => (
          <Button
            key={id}
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-sm"
            title={title}
            data-testid={`md-tool-${id}`}
            onClick={fn}
          >
            <Icon size={16} weight="bold" />
          </Button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-0 border border-t-0 border-border rounded-b-sm">
        <Textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          className="min-h-[420px] font-mono text-sm rounded-none border-0 md:border-r focus-visible:ring-0 resize-y"
          data-testid="md-textarea"
          placeholder="Write Markdown…"
        />
        <div className="min-h-[420px] max-h-[560px] overflow-auto p-4 bg-background" data-testid="md-preview">
          <MarkdownContent markdown={value} />
        </div>
      </div>
    </div>
  );
}
